import { useEffect } from 'react';

/**
 * A region a mouse can scroll must be reachable by a keyboard — the prototype's own rule.
 *
 * The frozen artifact does this at runtime (`m01-utils.js`, `makeScrollRegionsFocusable`): after
 * every render it walks its scroll containers and gives the ones that ACTUALLY overflow
 * `tabindex="0"`, so someone without a mouse can focus the box and use the arrow keys. A container
 * that fits gets nothing, because a focus stop that scrolls nowhere is noise in the tab order.
 *
 * The rebuild never needed it: no table overflowed. The register does now — the two LinkedIn
 * columns make it wider than a narrow viewport — and the accessibility comparison caught it
 * immediately, as a finding the reference does not have. So the rule is implemented, not the
 * symptom: every scroll container the prototype names is handled, on every screen, and the state is
 * re-derived on resize because whether a box overflows is a function of the viewport.
 *
 * WHY THE WORK IS COALESCED INTO ONE FRAME
 * ----------------------------------------
 * `scrollWidth` and `clientWidth` force the browser to lay the page out before it answers. Doing
 * that on every DOM mutation is fine on a register and ruinous on the designer, which moves layers
 * as the pointer moves: the first version of this hook measured on each mutation batch and the
 * authoring journey timed out. So mutations only SCHEDULE a pass, at most one per animation frame,
 * and the measurement itself happens where the browser was going to lay out anyway.
 *
 * Nothing visual changes: `tabindex` paints nothing, and the focus ring only appears once someone
 * has actually focused the region.
 */
const SCROLL_REGIONS = '.table-wrap, .code-view, .prop-tabs, .email-frame';

export function useScrollRegionsFocusable(): void {
  useEffect(() => {
    let frame = 0;
    const apply = (): void => {
      frame = 0;
      for (const el of document.querySelectorAll<HTMLElement>(SCROLL_REGIONS)) {
        const scrolls = el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
        if (scrolls) el.setAttribute('tabindex', '0');
        else if (el.getAttribute('tabindex') === '0') el.removeAttribute('tabindex');
      }
    };
    const schedule = (): void => {
      if (frame === 0) frame = window.requestAnimationFrame(apply);
    };
    schedule();
    /* Content arrives after the first paint — a register page, an import report — and a resize can
       turn a box that fitted into one that does not. Both schedule, neither measures inline. */
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);
}
